/**
 * Zustand Auth Store
 * Manages authentication state with localStorage persistence
 */

import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import type { AuthUser, AuthSession, AuthState, AuthActions } from "@/types";

/**
 * Storage key for localStorage persistence
 */
const AUTH_STORAGE_KEY = "motiv8m8-auth";

/**
 * Initial auth state
 */
const initialState: AuthState = {
  user: null,
  session: null,
  isLoading: true,
  isHydrated: false,
};

/**
 * Auth store with persistence
 * Uses Zustand's persist middleware for localStorage synchronization
 */
export const useAuthStore = create<AuthState & AuthActions>()(
  persist(
    (set) => ({
      ...initialState,

      /**
       * Set authenticated user
       */
      setUser: (user: AuthUser) =>
        set({
          user,
          session: {
            user,
            createdAt: new Date().toISOString(),
          },
        }),

      /**
       * Set full session data
       */
      setSession: (session: AuthSession) =>
        set({
          user: session.user,
          session,
        }),

      /**
       * Clear all auth data (logout)
       */
      clearAuth: () =>
        set({
          user: null,
          session: null,
        }),

      /**
       * Set loading state
       */
      setLoading: (isLoading: boolean) => set({ isLoading }),

      /**
       * Set hydration complete flag
       */
      setHydrated: (isHydrated: boolean) => set({ isHydrated }),
    }),
    {
      name: AUTH_STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      // Only persist user and session, not loading/hydrated flags
      partialize: (state) => ({
        user: state.user,
        session: state.session,
      }),
      // Handle rehydration from localStorage
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.setHydrated(true);
          state.setLoading(false);
        }
      },
    }
  )
);

// ============================================
// Selector hooks for optimized re-renders
// ============================================

/**
 * Get current authenticated user
 */
export const useAuthUser = () => useAuthStore((state) => state.user);

/**
 * Check if user is authenticated
 */
export const useIsAuthenticated = () =>
  useAuthStore((state) => state.user !== null);

/**
 * Get auth loading state
 */
export const useAuthLoading = () => useAuthStore((state) => state.isLoading);

/**
 * Check if store has been hydrated from localStorage
 */
export const useAuthHydrated = () => useAuthStore((state) => state.isHydrated);

/**
 * Get current session
 */
export const useAuthSession = () => useAuthStore((state) => state.session);

/**
 * Get auth actions (for components that need to dispatch)
 */
export const useAuthActions = () =>
  useAuthStore((state) => ({
    setUser: state.setUser,
    setSession: state.setSession,
    clearAuth: state.clearAuth,
    setLoading: state.setLoading,
    setHydrated: state.setHydrated,
  }));
